(() => {
  const controllerKey = Symbol.for('atease.materialDirectoryController');
  const existingController = document[controllerKey];
  if (existingController) {
    existingController.initAll(document);
    return;
  }

  const observers = new WeakMap();
  const directorySelector = '[data-material-directory]';

  function disableCarouselAutoplayForReducedMotion(section) {
    if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    section.querySelectorAll('[data-material-products]').forEach((carousel) => {
      const rawOptions = carousel.getAttribute('data-flickityt4s-js');
      if (!rawOptions) return;

      try {
        const options = JSON.parse(rawOptions);
        options.autoPlay = 0;
        carousel.setAttribute('data-flickityt4s-js', JSON.stringify(options));
      } catch (_) {
        // Leave malformed theme data untouched so the theme can report it normally.
      }
    });
  }

  function initMaterialDirectory(section) {
    disableCarouselAutoplayForReducedMotion(section);
    initProgressTracks(section);
    if (observers.has(section) || !('IntersectionObserver' in window)) return;

    const links = section.querySelectorAll('[data-material-link]');
    const chapters = section.querySelectorAll('[data-material-chapter]');
    if (!links.length || !chapters.length) return;

    const visibility = new Map();
    chapters.forEach((chapter, order) => {
      visibility.set(chapter, {
        target: chapter,
        isIntersecting: false,
        intersectionRatio: 0,
        order,
      });
    });

    const observer = new IntersectionObserver((entries) => {
      const updateActiveLink = (visibleHandle) => {
        links.forEach((link) => {
          const isActive = link.dataset.materialLink === visibleHandle;
          link.classList.toggle('is-active', isActive);
          if (isActive) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      };
      entries.forEach((entry) => {
        const previous = visibility.get(entry.target);
        if (!previous) return;
        visibility.set(entry.target, {
          target: entry.target,
          isIntersecting: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio,
          order: previous.order,
        });
      });
      const visible = [...visibility.values()]
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => (
          b.intersectionRatio - a.intersectionRatio || a.order - b.order
        ))[0];

      // Preserve the current link until another chapter enters the observer band.
      if (!visible) return;
      const visibleHandle = visible.target.dataset.materialChapter;
      updateActiveLink(visibleHandle);
    }, {
      rootMargin: '-18% 0px -62% 0px',
      threshold: [0, 0.2, 0.5],
    });

    chapters.forEach((chapter) => observer.observe(chapter));
    observers.set(section, { observer, visibility });
  }

  function initProgressTracks(section) {
    section.querySelectorAll('[data-material-progress]:not([data-progress-ready])').forEach((track) => {
      const carousel = track.closest('[data-material-products]');
      if (!carousel) return;
      const getJqCarousel = () => {
        const jq = window.jQuery_T4NT || window.jQuery;
        return jq ? jq(carousel) : null;
      };
      const getCarousel = () => getJqCarousel()?.data('flickityt4s') || null;
      const getMetrics = () => {
        const total = Math.max(carousel.querySelectorAll('.t4s-col-item').length, 1);
        const visible = Math.max(carousel.querySelectorAll('.t4s-col-item.is-selected').length, 1);
        const flkty = getCarousel();
        const groupCount = Math.max(flkty?.slides?.length || Math.ceil(total / visible), 1);
        return { flkty, groupCount, lastIndex: groupCount - 1 };
      };
      const selectAtPointer = (clientX) => {
        const rect = track.getBoundingClientRect();
        const { flkty, lastIndex } = getMetrics();
        if (!flkty || !lastIndex || rect.width <= 0) return;
        const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
        flkty.select(Math.round(ratio * lastIndex));
      };
      let wheelDeltaX = 0;
      let wheelGestureHandled = false;
      let lastWheelAt = -Infinity;
      carousel.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        const eventTime = Number.isFinite(event.timeStamp) ? event.timeStamp : 0;
        if (eventTime - lastWheelAt > 160) {
          wheelDeltaX = 0;
          wheelGestureHandled = false;
        }
        lastWheelAt = eventTime;
        if (wheelGestureHandled) return;

        const deltaScale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerWidth : 1;
        wheelDeltaX += event.deltaX * deltaScale;
        if (Math.abs(wheelDeltaX) < 24) return;

        const { flkty, lastIndex } = getMetrics();
        if (!flkty) return;
        const next = Math.min(Math.max(flkty.selectedIndex + Math.sign(wheelDeltaX), 0), lastIndex);
        if (next !== flkty.selectedIndex) flkty.select(next);
        wheelDeltaX = 0;
        wheelGestureHandled = true;
      }, { passive: false });
      let pointerId = null;
      track.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 && event.pointerType === 'mouse') return;
        pointerId = event.pointerId;
        track.setPointerCapture?.(pointerId);
        event.preventDefault();
        selectAtPointer(event.clientX);
      });
      track.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) return;
        event.preventDefault();
        selectAtPointer(event.clientX);
      });
      const releasePointer = (event) => {
        if (pointerId !== event.pointerId) return;
        pointerId = null;
        track.releasePointerCapture?.(event.pointerId);
      };
      track.addEventListener('pointerup', releasePointer);
      track.addEventListener('pointercancel', releasePointer);
      track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const flkty = getCarousel();
        if (!flkty) return;
        const { lastIndex } = getMetrics();
        const current = flkty.selectedIndex;
        const next = event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? lastIndex
            : Math.min(Math.max(current + (event.key === 'ArrowRight' ? 1 : -1), 0), lastIndex);
        event.preventDefault();
        flkty.select(next);
      });
      const updateValue = () => {
        const { flkty, groupCount } = getMetrics();
        if (!flkty) return;
        track.setAttribute('aria-valuemax', String(groupCount));
        track.setAttribute('aria-valuenow', String(flkty.selectedIndex + 1));
      };
      getJqCarousel()?.on('select.flickityt4s.materialProgress', updateValue);
      track.setAttribute('data-progress-ready', 'true');
      updateValue();
    });
  }

  function directoriesIn(scope) {
    const directories = [];
    if (scope.matches?.(directorySelector)) directories.push(scope);
    scope.querySelectorAll?.(directorySelector).forEach((section) => directories.push(section));
    return directories;
  }

  function initAll(scope = document) {
    directoriesIn(scope).forEach(initMaterialDirectory);
  }

  function unloadAll(scope) {
    directoriesIn(scope).forEach((section) => {
      const state = observers.get(section);
      if (!state) return;
      state.observer.disconnect();
      state.visibility.clear();
      observers.delete(section);
    });
  }

  const controller = { initAll, unloadAll };
  Object.defineProperty(document, controllerKey, {
    configurable: true,
    value: controller,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => controller.initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => controller.unloadAll(event.target));
})();
